import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit as limitQuery,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { safeTimestampToDate } from '../utils/timestampUtils';
import { AttendanceDataMigrationService } from './attendanceDataMigrationService';
import { 
  AttendanceRecord, 
  AttendanceSession,
  AttendanceReport,
  Class,
  Subject,
  Student,
  User
} from '../types';

export class AttendanceService {
  private static sessionsCollection = 'attendanceSessions';
  private static recordsCollection = 'attendanceRecords';

  // Create a new attendance session for a specific subject
  static async createAttendanceSession(
    classId: string,
    subjectId: string,
    facultyId: string,
    date: Date = new Date(),
    sessionType: 'lecture' | 'practical' | 'tutorial' | 'exam' = 'lecture'
  ): Promise<string> {
    try {
      const db = getDB();
      // Get class, subject, and faculty details
      const [classDoc, subjectDoc, facultyDoc] = await Promise.all([
        getDoc(doc(db, 'classes', classId)),
        getDoc(doc(db, 'subjects', subjectId)),
        getDoc(doc(db, 'users', facultyId))
      ]);

      if (!classDoc.exists()) {
        throw new Error('Class not found');
      }

      if (!subjectDoc.exists()) {
        throw new Error('Subject not found');
      }

      if (!facultyDoc.exists()) {
        throw new Error('Faculty not found');
      }

      const classData = classDoc.data() as Class;
      const subjectData = subjectDoc.data() as Subject;
      const facultyData = facultyDoc.data() as User;

      const facultyName = `${facultyData.firstName || ''} ${facultyData.lastName || ''}`.trim() || facultyData.username;

      const session: Omit<AttendanceSession, 'id'> = {
        classId,
        className: classData.name,
        subjectId,
        subjectName: subjectData.name,
        subjectCode: subjectData.code,
        date,
        facultyId,
        facultyName,
        records: [],
        totalStudents: classData.students.length,
        presentCount: 0,
        absentCount: classData.students.length,
        attendanceRate: 0,
        isCompleted: false,
        sessionType,
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.sessionsCollection), {
        ...session,
        date: Timestamp.fromDate(session.date),
        createdAt: Timestamp.fromDate(session.createdAt)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating attendance session:', error);
      throw new Error('Failed to create attendance session');
    }
  }

  // Mark attendance for students in a session
  static async markAttendance(
    sessionId: string,
    attendanceData: { studentId: string; isPresent: boolean; remarks?: string }[]
  ): Promise<void> {
    try {
      const db = getDB();
      const sessionDoc = await getDoc(doc(db, this.sessionsCollection, sessionId));
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }

      const session = { id: sessionDoc.id, ...sessionDoc.data() } as AttendanceSession;
      const batch = writeBatch(db);

      // Get class data to get student details
      const classDoc = await getDoc(doc(db, 'classes', session.classId));
      if (!classDoc.exists()) {
        throw new Error('Class not found');
      }

      const classData = classDoc.data() as Class;
      const records: AttendanceRecord[] = [];

      for (const attendance of attendanceData) {
        const student = classData.students.find(s => s.id === attendance.studentId);
        if (!student) continue;

        const record: Omit<AttendanceRecord, 'id'> = {
          studentId: attendance.studentId,
          studentName: student.name,
          studentRollNumber: student.rollNumber,
          classId: session.classId,
          className: session.className,
          subjectId: session.subjectId,
          subjectName: session.subjectName,
          subjectCode: session.subjectCode,
          date: session.date,
          isPresent: attendance.isPresent,
          markedBy: session.facultyId,
          markedByName: session.facultyName,
          markedAt: new Date(),
          remarks: attendance.remarks
        };

        // Add to batch
        const recordRef = doc(collection(db, this.recordsCollection));
        batch.set(recordRef, {
          ...record,
          date: Timestamp.fromDate(record.date),
          markedAt: Timestamp.fromDate(record.markedAt)
        });

        records.push({ ...record, id: recordRef.id });
      }

      // Update session with attendance summary
      const presentCount = attendanceData.filter(a => a.isPresent).length;
      const absentCount = attendanceData.length - presentCount;
      const attendanceRate = attendanceData.length > 0 ? (presentCount / attendanceData.length) * 100 : 0;

      batch.update(doc(db, this.sessionsCollection, sessionId), {
        records: records,
        presentCount,
        absentCount,
        attendanceRate: Math.round(attendanceRate * 100) / 100,
        isCompleted: true,
        updatedAt: Timestamp.fromDate(new Date())
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw new Error('Failed to mark attendance');
    }
  }

  // Get attendance sessions with filters
  static async getAttendanceSessions(
    filters: {
      classId?: string;
      subjectId?: string;
      facultyId?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
    } = {}
  ): Promise<AttendanceSession[]> {
    try {
      const db = getDB();
      let q = collection(db, this.sessionsCollection);
      
      const constraints: any[] = [orderBy('date', 'desc')];

      if (filters.classId) {
        constraints.push(where('classId', '==', filters.classId));
      }

      if (filters.subjectId) {
        constraints.push(where('subjectId', '==', filters.subjectId));
      }

      if (filters.facultyId) {
        constraints.push(where('facultyId', '==', filters.facultyId));
      }

      if (filters.startDate) {
        constraints.push(where('date', '>=', Timestamp.fromDate(filters.startDate)));
      }

      if (filters.endDate) {
        constraints.push(where('date', '<=', Timestamp.fromDate(filters.endDate)));
      }

      if (filters.limit) {
        constraints.push(limitQuery(filters.limit));
      }

      const querySnapshot = await getDocs(query(q, ...constraints));
      const sessions: AttendanceSession[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          ...data,
          date: safeTimestampToDate(data.date),
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        } as AttendanceSession);
      });

      return sessions;
    } catch (error) {
      console.error('Error fetching attendance sessions:', error);
      throw new Error('Failed to fetch attendance sessions');
    }
  }

  // Get attendance records with filters
  static async getAttendanceRecords(
    filters: {
      classId?: string;
      subjectId?: string;
      studentId?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
    } = {}
  ): Promise<AttendanceRecord[]> {
    try {
      const db = getDB();
      let q = collection(db, this.recordsCollection);
      
      const constraints: any[] = [orderBy('date', 'desc')];

      if (filters.classId) {
        constraints.push(where('classId', '==', filters.classId));
      }

      if (filters.subjectId) {
        constraints.push(where('subjectId', '==', filters.subjectId));
      }

      if (filters.studentId) {
        constraints.push(where('studentId', '==', filters.studentId));
      }

      if (filters.startDate) {
        constraints.push(where('date', '>=', Timestamp.fromDate(filters.startDate)));
      }

      if (filters.endDate) {
        constraints.push(where('date', '<=', Timestamp.fromDate(filters.endDate)));
      }

      if (filters.limit) {
        constraints.push(limitQuery(filters.limit));
      }

      const querySnapshot = await getDocs(query(q, ...constraints));
      const records: AttendanceRecord[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        records.push({
          id: doc.id,
          ...data,
          date: safeTimestampToDate(data.date),
          markedAt: safeTimestampToDate(data.markedAt),
        } as AttendanceRecord);
      });

      return records;
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      throw new Error('Failed to fetch attendance records');
    }
  }

  // Get student attendance summary for a subject
  static async getStudentSubjectAttendance(
    studentId: string,
    subjectId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    totalSessions: number;
    presentSessions: number;
    attendanceRate: number;
    records: AttendanceRecord[];
  }> {
    try {
      const filters: any = {
        studentId,
        subjectId,
        startDate,
        endDate
      };

      const records = await this.getAttendanceRecords(filters);
      const presentSessions = records.filter(r => r.isPresent).length;
      const totalSessions = records.length;
      const attendanceRate = totalSessions > 0 ? (presentSessions / totalSessions) * 100 : 0;

      return {
        totalSessions,
        presentSessions,
        attendanceRate: Math.round(attendanceRate * 100) / 100,
        records
      };
    } catch (error) {
      console.error('Error fetching student subject attendance:', error);
      throw new Error('Failed to fetch student subject attendance');
    }
  }

  // Get class subject attendance summary
  static async getClassSubjectAttendance(
    classId: string,
    subjectId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    totalSessions: number;
    averageAttendanceRate: number;
    studentAttendance: {
      studentId: string;
      studentName: string;
      rollNumber: string;
      totalSessions: number;
      presentSessions: number;
      attendanceRate: number;
    }[];
  }> {
    try {
      const sessions = await this.getAttendanceSessions({
        classId,
        subjectId,
        startDate,
        endDate
      });

      const records = await this.getAttendanceRecords({
        classId,
        subjectId,
        startDate,
        endDate
      });

      // Group records by student
      const studentRecords = records.reduce((acc, record) => {
        if (!acc[record.studentId]) {
          acc[record.studentId] = {
            studentId: record.studentId,
            studentName: record.studentName,
            rollNumber: record.studentRollNumber,
            records: []
          };
        }
        acc[record.studentId].records.push(record);
        return acc;
      }, {} as any);

      const studentAttendance = Object.values(studentRecords).map((student: any) => {
        const presentSessions = student.records.filter((r: AttendanceRecord) => r.isPresent).length;
        const totalSessions = student.records.length;
        const attendanceRate = totalSessions > 0 ? (presentSessions / totalSessions) * 100 : 0;

        return {
          studentId: student.studentId,
          studentName: student.studentName,
          rollNumber: student.rollNumber,
          totalSessions,
          presentSessions,
          attendanceRate: Math.round(attendanceRate * 100) / 100
        };
      });

      const averageAttendanceRate = studentAttendance.length > 0 
        ? studentAttendance.reduce((sum, s) => sum + s.attendanceRate, 0) / studentAttendance.length
        : 0;

      return {
        totalSessions: sessions.length,
        averageAttendanceRate: Math.round(averageAttendanceRate * 100) / 100,
        studentAttendance
      };
    } catch (error) {
      console.error('Error fetching class subject attendance:', error);
      throw new Error('Failed to fetch class subject attendance');
    }
  }

  // Get attendance statistics for analytics
  static async getAttendanceStats(
    filters: {
      classId?: string;
      subjectId?: string;
      facultyId?: string;
      startDate?: Date;
      endDate?: Date;
    } = {}
  ): Promise<{
    totalSessions: number;
    totalStudents: number;
    overallAttendanceRate: number;
    subjectWiseStats: {
      subjectId: string;
      subjectName: string;
      subjectCode: string;
      totalSessions: number;
      averageAttendanceRate: number;
    }[];
    dailyStats: {
      date: string;
      totalSessions: number;
      averageAttendanceRate: number;
    }[];
  }> {
    try {
      const sessions = await this.getAttendanceSessions(filters);
      const records = await this.getAttendanceRecords(filters);

      // Calculate overall stats
      const totalSessions = sessions.length;
      const totalAttendanceRecords = records.length;
      const presentRecords = records.filter(r => r.isPresent).length;
      const overallAttendanceRate = totalAttendanceRecords > 0 
        ? (presentRecords / totalAttendanceRecords) * 100 
        : 0;

      // Calculate subject-wise stats
      const subjectStats = sessions.reduce((acc, session) => {
        if (!acc[session.subjectId]) {
          acc[session.subjectId] = {
            subjectId: session.subjectId,
            subjectName: session.subjectName,
            subjectCode: session.subjectCode,
            sessions: [],
            totalAttendanceRate: 0
          };
        }
        acc[session.subjectId].sessions.push(session);
        acc[session.subjectId].totalAttendanceRate += session.attendanceRate;
        return acc;
      }, {} as any);

      const subjectWiseStats = Object.values(subjectStats).map((subject: any) => ({
        subjectId: subject.subjectId,
        subjectName: subject.subjectName,
        subjectCode: subject.subjectCode,
        totalSessions: subject.sessions.length,
        averageAttendanceRate: subject.sessions.length > 0 
          ? Math.round((subject.totalAttendanceRate / subject.sessions.length) * 100) / 100
          : 0
      }));

      // Calculate daily stats
      const dailyStats = sessions.reduce((acc, session) => {
        const dateKey = session.date.toISOString().split('T')[0];
        if (!acc[dateKey]) {
          acc[dateKey] = {
            date: dateKey,
            sessions: [],
            totalAttendanceRate: 0
          };
        }
        acc[dateKey].sessions.push(session);
        acc[dateKey].totalAttendanceRate += session.attendanceRate;
        return acc;
      }, {} as any);

      const dailyStatsArray = Object.values(dailyStats).map((day: any) => ({
        date: day.date,
        totalSessions: day.sessions.length,
        averageAttendanceRate: day.sessions.length > 0 
          ? Math.round((day.totalAttendanceRate / day.sessions.length) * 100) / 100
          : 0
      })).sort((a, b) => a.date.localeCompare(b.date));

      const totalStudents = new Set(records.map(r => r.studentId)).size;

      return {
        totalSessions,
        totalStudents,
        overallAttendanceRate: Math.round(overallAttendanceRate * 100) / 100,
        subjectWiseStats,
        dailyStats: dailyStatsArray
      };
    } catch (error) {
      console.error('Error fetching attendance stats:', error);
      throw new Error('Failed to fetch attendance stats');
    }
  }

  // Delete attendance session
  static async deleteAttendanceSession(sessionId: string): Promise<void> {
    try {
      const db = getDB();
      const batch = writeBatch(db);
      
      // Delete session
      batch.delete(doc(db, this.sessionsCollection, sessionId));
      
      // Delete associated records
      const records = await this.getAttendanceRecords({});
      const sessionRecords = records.filter(r => r.id === sessionId);
      
      sessionRecords.forEach(record => {
        batch.delete(doc(db, this.recordsCollection, record.id));
      });

      await batch.commit();
    } catch (error) {
      console.error('Error deleting attendance session:', error);
      throw new Error('Failed to delete attendance session');
    }
  }

  // Update attendance session
  static async updateAttendanceSession(
    sessionId: string, 
    updates: Partial<AttendanceSession>
  ): Promise<void> {
    try {
      const db = getDB();
      const updateData: any = { ...updates };
      
      if (updateData.date) {
        updateData.date = Timestamp.fromDate(updateData.date);
      }
      
      updateData.updatedAt = Timestamp.fromDate(new Date());
      
      await updateDoc(doc(db, this.sessionsCollection, sessionId), updateData);
    } catch (error) {
      console.error('Error updating attendance session:', error);
      throw new Error('Failed to update attendance session');
    }
  }

  // Save subject-based attendance directly without session creation
  static async saveSubjectAttendance(
    subjectId: string,
    date: string,
    attendanceData: Omit<AttendanceRecord, 'id'>[]
  ): Promise<void> {
    try {
      const db = getDB();
      const batch = writeBatch(db);
      const attendanceDate = new Date(date);

      // Create individual attendance records
      for (const record of attendanceData) {
        const recordRef = doc(collection(db, this.recordsCollection));
        batch.set(recordRef, {
          ...record,
          date: Timestamp.fromDate(attendanceDate),
          markedAt: Timestamp.fromDate(record.markedAt)
        });
      }

      // Create a summary session record
      const presentCount = attendanceData.filter(r => r.isPresent).length;
      const totalStudents = attendanceData.length;
      const attendanceRate = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;

      const sessionData = {
        classId: 'multiple', // Since subject can have students from multiple classes
        className: 'Subject-based Session',
        subjectId: subjectId,
        subjectName: attendanceData[0]?.subjectName || '',
        subjectCode: attendanceData[0]?.subjectCode || '',
        date: Timestamp.fromDate(attendanceDate),
        facultyId: attendanceData[0]?.markedBy || '',
        facultyName: attendanceData[0]?.markedByName || '',
        records: [], // We don't store individual records in session for subject-based
        totalStudents,
        presentCount,
        absentCount: totalStudents - presentCount,
        attendanceRate: Math.round(attendanceRate * 100) / 100,
        isCompleted: true,
        sessionType: 'lecture' as const,
        createdAt: Timestamp.fromDate(new Date())
      };

      const sessionRef = doc(collection(db, this.sessionsCollection));
      batch.set(sessionRef, sessionData);

      await batch.commit();
    } catch (error) {
      console.error('Error saving subject attendance:', error);
      throw new Error('Failed to save subject attendance');
    }
  }

  // Save subject-based attendance directly to subjects collection
  static async saveSubjectAttendanceToSubjectsCollection(
    subjectId: string,
    date: string,
    attendanceData: {
      studentId: string;
      studentName: string;
      rollNumber: string;
      isPresent: boolean;
      remarks?: string;
    }[],
    facultyId: string,
    facultyName: string
  ): Promise<void> {
    try {
      const db = getDB();
      const attendanceDate = new Date(date);
      const dateKey = attendanceDate.toISOString().split('T')[0]; // YYYY-MM-DD format

      // Get the subject document
      const subjectRef = doc(db, 'subjects', subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (!subjectDoc.exists()) {
        throw new Error('Subject not found');
      }

      const subjectData = subjectDoc.data();
      
      // Calculate attendance statistics
      const presentCount = attendanceData.filter(s => s.isPresent).length;
      const totalStudents = attendanceData.length;
      const attendanceRate = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;

      // Prepare attendance record for this date
      const attendanceRecord = {
        date: dateKey,
        markedBy: facultyId,
        markedByName: facultyName,
        markedAt: Timestamp.fromDate(new Date()),
        students: attendanceData.map(student => ({
          id: student.studentId,
          name: student.studentName,
          rollNumber: student.rollNumber,
          status: student.isPresent ? 'Present' : 'Absent',
          remarks: student.remarks || ''
        })),
        presentCount: presentCount,
        absentCount: totalStudents - presentCount,
        attendanceRate: Math.round(attendanceRate * 100) / 100
      };

      // Get existing attendance data or initialize
      const existingAttendance = subjectData.attendance || {};
      
      // Update attendance for this date
      existingAttendance[dateKey] = attendanceRecord;

      // Update the subject document
      await updateDoc(subjectRef, {
        attendance: existingAttendance,
        lastUpdated: Timestamp.fromDate(new Date())
      });

      // Also save to the traditional collections for backward compatibility
      await this.saveSubjectAttendance(subjectId, date, attendanceData.map(student => ({
        studentId: student.studentId,
        studentName: student.studentName,
        studentRollNumber: student.rollNumber,
        classId: '', // Will be filled from subject data
        className: '',
        subjectId: subjectId,
        subjectName: subjectData.name || '',
        subjectCode: subjectData.code || '',
        date: attendanceDate,
        isPresent: student.isPresent,
        markedBy: facultyId,
        markedByName: facultyName,
        markedAt: new Date(),
        remarks: student.remarks
      })));

    } catch (error) {
      console.error('Error saving subject attendance to subjects collection:', error);
      throw new Error('Failed to save subject attendance');
    }
  }

  // Get subject attendance from subjects collection
  static async getSubjectAttendanceFromSubjectsCollection(
    subjectId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    subjectId: string;
    subjectName: string;
    subjectCode: string;
    attendanceData: {
      date: string;
      presentCount: number;
      absentCount: number;
      attendanceRate: number;
      markedBy: string;
      markedByName: string;
      markedAt: Date;
      students: {
        id: string;
        name: string;
        rollNumber: string;
        status: 'Present' | 'Absent';
        remarks: string;
      }[];
    }[];
    totalSessions: number;
    averageAttendanceRate: number;
  }> {
    try {
      // Use the migration service to handle both legacy and modern formats
      return await AttendanceDataMigrationService.readLegacySubjectAttendance(
        subjectId,
        startDate,
        endDate
      );
    } catch (error) {
      console.error('Error getting subject attendance from subjects collection:', error);
      throw new Error('Failed to get subject attendance');
    }
  }

  // Get student attendance for a specific subject from subjects collection
  static async getStudentSubjectAttendanceFromSubjectsCollection(
    subjectId: string,
    studentId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    studentId: string;
    studentName: string;
    rollNumber: string;
    subjectId: string;
    subjectName: string;
    subjectCode: string;
    totalSessions: number;
    presentSessions: number;
    attendanceRate: number;
    attendanceRecords: {
      date: string;
      status: 'Present' | 'Absent';
      remarks: string;
      markedBy: string;
      markedByName: string;
    }[];
  }> {
    try {
      // Use the migration service to handle both legacy and modern formats
      return await AttendanceDataMigrationService.readLegacyStudentSubjectAttendance(
        subjectId,
        studentId,
        startDate,
        endDate
      );
    } catch (error) {
      console.error('Error getting student subject attendance from subjects collection:', error);
      throw new Error('Failed to get student subject attendance');
    }
  }

  // Get attendance statistics for multiple subjects from subjects collection
  static async getSubjectsAttendanceStats(
    subjectIds: string[],
    startDate?: string,
    endDate?: string
  ): Promise<{
    totalSubjects: number;
    totalSessions: number;
    overallAttendanceRate: number;
    subjectStats: {
      subjectId: string;
      subjectName: string;
      subjectCode: string;
      totalSessions: number;
      averageAttendanceRate: number;
      totalStudents: number;
    }[];
  }> {
    try {
      const subjectStats: any[] = [];
      let totalSessions = 0;
      let totalAttendanceRate = 0;

      for (const subjectId of subjectIds) {
        try {
          const subjectAttendance = await this.getSubjectAttendanceFromSubjectsCollection(
            subjectId,
            startDate,
            endDate
          );

          const uniqueStudents = new Set();
          subjectAttendance.attendanceData.forEach(session => {
            session.students.forEach(student => uniqueStudents.add(student.id));
          });

          subjectStats.push({
            subjectId: subjectAttendance.subjectId,
            subjectName: subjectAttendance.subjectName,
            subjectCode: subjectAttendance.subjectCode,
            totalSessions: subjectAttendance.totalSessions,
            averageAttendanceRate: subjectAttendance.averageAttendanceRate,
            totalStudents: uniqueStudents.size
          });

          totalSessions += subjectAttendance.totalSessions;
          totalAttendanceRate += subjectAttendance.averageAttendanceRate;
        } catch (error) {
          console.warn(`Failed to get stats for subject ${subjectId}:`, error);
        }
      }

      const overallAttendanceRate = subjectStats.length > 0 
        ? totalAttendanceRate / subjectStats.length 
        : 0;

      return {
        totalSubjects: subjectStats.length,
        totalSessions,
        overallAttendanceRate: Math.round(overallAttendanceRate * 100) / 100,
        subjectStats
      };

    } catch (error) {
      console.error('Error getting subjects attendance stats:', error);
      throw new Error('Failed to get subjects attendance stats');
    }
  }
} 